import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_REPORTS_ASSESSMENT_PATH_KEY } from '@/swagger/constants/reports-keys';
import { API_GET_REPORTS_ASSESSMENTS, KEY_GET_REPORTS_ASSESSMENTS } from '../constants';
import { MOCK_RESPONSE_GET_REPORTS_ASSESSMENTS } from '../mocks';

type GetReportsAssessments = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_REPORTS_ASSESSMENT_PATH_KEY
>;

type RequestGetReportsAssessmentsQuery = GetReportsAssessments['request']['query'];

type ResponseGetReportsAssessments = GetReportsAssessments['response']['body'];

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetReportsAssessments, 'successSaga' | 'errorSaga'>;
};

export type CallParamsGetReportsAssessments = RequestGetReportsAssessmentsQuery & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetReportsAssessments = ResponseGetReportsAssessments;

type CallErrorGetReportsAssessments = {
  message: string;
  code: number;
};

type CallSelectorsGetReportsAssessments = {
  data: ResponseGetReportsAssessments['data'];
  totalItems: ResponseGetReportsAssessments['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload: Omit<CallParamsGetReportsAssessments, 'successSaga' | 'errorSaga'> =
  {} as Omit<CallParamsGetReportsAssessments, 'successSaga' | 'errorSaga'>;

export const getReportsAssessmentsCall = createGeneralCall<
  CallParamsGetReportsAssessments,
  CallResponseGetReportsAssessments,
  CallErrorGetReportsAssessments,
  CallSelectorsGetReportsAssessments
>({
  id: KEY_GET_REPORTS_ASSESSMENTS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_REPORTS_ASSESSMENTS(org as unknown as string, payload);
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_REPORTS_ASSESSMENTS : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  cancellable: true,
});
